import React, { useRef } from 'react';
import { Box, Flex, Text } from 'rebass/styled-components';
import PropTypes from 'prop-types';
import { ChevronDown, Pen } from 'lucide-react';
import { Card } from 'components/atoms/Card';
import { ProfilePicture } from '@getro/rombo';
import { Link } from 'react-router-dom';
import { Dropdown } from 'components/molecules/dropdown';
import ContentLoader from 'react-content-loader';
import { useTheme } from 'styled-components';
import { ContactOfWithEmail } from './contactOfWithEmail';

export const ContactOfRenderer = ({ data, value, column }) => {
  const ref = useRef(null);
  const { colors } = useTheme();
  if (!data?.original) {
    return '';
  }

  if (data.options.showLoader) {
    return (
      <ContentLoader
        speed={2}
        width={400}
        height={120}
        viewBox="0 0 400 120"
        backgroundColor={colors.neutral[50]}
        foregroundColor={colors.neutral[20]}
      >
        <rect x="47" y="20" rx="4" ry="4" width="80" height="8" />
        <circle cx="23" cy="25" r="13" />
      </ContentLoader>
    );
  }

  if (!value) {
    return '';
  }

  return (
    <Flex
      p="8px"
      alignItems="center"
      sx={{
        height: '100%',
        alignItems: 'center',
        gap: '8px',
        position: 'relative',
        justifyContent: 'space-between',
        width: '100%',
        '&:hover': {
          '#chevron-down': {
            display: 'block',
          },
        },
      }}
    >
      <Flex sx={{ gap: '8px', alignItems: 'center' }}>
        <Flex minWidth="fit-content">
          {value.slice(0, 3).map((user, index, array) => (
            <ProfilePicture
              width={24}
              sx={{
                width: '24px',
                height: '24px',
                borderRadius: '24px',
                background: 'neutral.0',
                mr: index === array.length - 1 ? '0px' : '-8px',
                boxShadow: '0px 0px 0px 2px #FFFFFF, 0px 0px 0px 2px #DEE4ED',
              }}
              key={user.id}
              name={index === 2 && value.length > 3 ? `+ ${value.length - 2}` : user.name}
              size="24px"
              imageUrl={index === 2 && value.length > 3 ? null : user.avatarUrl}
            />
          ))}
        </Flex>
        <Text
          sx={{
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            fontSize: '14px',
            fontWeight: '400',
            lineHeight: '16px',
          }}
        >
          {value.map((importer) => importer.name).join(', ')}
        </Text>
      </Flex>
      <Dropdown
        ref={ref}
        placement="bottom-end"
        trigger={
          <Box
            as={ChevronDown}
            id="chevron-down"
            minWidth="16px"
            size="16px"
            sx={{ color: 'text.main', display: 'none', cursor: 'pointer' }}
          />
        }
      >
        <Card
          width={Math.max(column.getActualWidth(), 280)}
          p="0px"
          py="8px"
          sx={{
            transform: 'translate(9px, 12px)',
          }}
        >
          {value.length === 0 && (
            <Flex
              sx={{
                gap: '8px',
                py: '8px',
                px: '16px',
              }}
            >
              <Text
                sx={{
                  fontSize: '14px',
                  fontWeight: '400',
                  lineHeight: '16px',
                  color: 'text.subtle',
                }}
              >
                Not a contact of any team member.
              </Text>
            </Flex>
          )}

          {value.length > 0 &&
            value.map((importer) => (
              <ContactOfWithEmail width={32} key={importer.id} user={importer} type={data.options.type} />
            ))}

          <Box
            as={Link}
            to={data.options.contactUrl}
            sx={{ textDecoration: 'none' }}
            onClick={() => ref.current.closeDropdown()}
          >
            <Flex
              sx={{
                gap: '8px',
                py: '8px',
                px: '8px',
                mt: '8px',
                mx: '8px',
                alignItems: 'center',
                cursor: 'pointer',
                borderTop: '1px solid',
                borderColor: 'border.subtle',
              }}
            >
              <Box as={Pen} size="16px" sx={{ color: 'text.main' }} />
              <Text
                sx={{
                  fontSize: '14px',
                  fontWeight: '400',
                  lineHeight: '16px',
                }}
              >
                Edit
              </Text>
            </Flex>
          </Box>
        </Card>
      </Dropdown>
    </Flex>
  );
};

ContactOfRenderer.propTypes = {
  data: PropTypes.object,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.array, PropTypes.bool, PropTypes.object]),
  column: PropTypes.object,
};

ContactOfRenderer.defaultProps = {
  data: {},
  value: '',
  column: {},
};
